import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Trophy, Vote, Undo2, MapPin, Music, Share2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function ShareNominee({ n, s, c }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/awards/${s.slug}/${c.slug}`;
  const text = `🏆 Vote for ${n.name} in "${c.name}" at The VIP Spotlight Awards — celebrating the best of the Gulf Coast!`;

  const share = async (e) => {
    e.stopPropagation();
    if (navigator.share) {
      try { await navigator.share({ title: "The VIP Spotlight Awards", text, url }); return; } catch { /* cancelled */ }
    }
    setOpen((o) => !o);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(`${text} ${url}`); } catch {
      const ta = document.createElement("textarea"); ta.value = `${text} ${url}`; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    setCopied(true); toast.success("Link copied — share it anywhere!");
    setTimeout(() => { setCopied(false); setOpen(false); }, 1200);
  };
  const ext = (href) => { window.open(href, "_blank", "noopener,width=600,height=500"); setOpen(false); };

  return (
    <div className="relative">
      <button onClick={share} title="Share this nomination" className="text-xs border border-white/15 hover:border-amber-400/50 text-zinc-300 hover:text-amber-300 px-2.5 py-1.5 rounded-full flex items-center gap-1 transition-colors" data-testid={`share-${n.slug}`}>
        <Share2 size={11} /> Share
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute bottom-full right-0 mb-2 z-40 bg-[#1a1a1a] border border-white/15 rounded-xl p-2 w-44 shadow-xl" data-testid={`share-menu-${n.slug}`}>
            <button onClick={() => ext(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`)} className="w-full text-left text-xs px-3 py-2 rounded-lg hover:bg-white/5" data-testid={`share-x-${n.slug}`}>Share on X</button>
            <button onClick={() => ext(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`)} className="w-full text-left text-xs px-3 py-2 rounded-lg hover:bg-white/5" data-testid={`share-fb-${n.slug}`}>Share on Facebook</button>
            <button onClick={copy} className="w-full text-left text-xs px-3 py-2 rounded-lg hover:bg-white/5 flex items-center gap-1.5" data-testid={`share-copy-${n.slug}`}>
              {copied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />} {copied ? "Copied!" : "Copy link"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function AwardCategory() {
  const { season, category } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const load = useCallback(() => api.get(`/awards/seasons/${season}/categories/${category}`).then((r) => setData(r.data)).catch(() => setErr(true)), [season, category]);
  useEffect(() => { load(); }, [load]);

  if (err) return <p className="text-zinc-500 py-24 text-center">Category not found.</p>;
  if (!data) return <p className="text-zinc-500 py-24 text-center">Loading…</p>;
  const { season: s, category: c, nominees, settings, result } = data;
  const votingOpen = s.flags.voting_open;

  const vote = async (n) => {
    if (!user) { toast.info("Log in to vote"); nav("/login"); return; }
    try {
      const { data: r } = await api.post("/awards/vote", { season_id: s.id, category_id: c.id, entity_type: n.entity_type, entity_id: n.id });
      toast.success(r.message); load();
    } catch (e) { toast.error(errMsg(e)); load(); }
  };
  const unvote = async (n) => {
    try {
      const { data: r } = await api.post("/awards/unvote", { category_id: c.id, entity_id: n.id });
      toast.success(r.message); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="px-6 md:px-12 lg:px-20 py-12" data-testid="award-category-page">
      <Link to={`/awards/${s.slug}`} className="text-sm text-zinc-400 hover:text-white">← {s.name}</Link>
      <div className="mt-4 mb-8">
        <p className="overline mb-1 flex items-center gap-2"><Trophy size={13} className="text-amber-400" /> The VIP Spotlight Awards</p>
        <h1 className="font-display font-black text-4xl md:text-5xl">{c.name}</h1>
        {c.description && <p className="text-zinc-400 mt-2 max-w-2xl">{c.description}</p>}
        <div className="mt-3 text-sm">
          {votingOpen ? <span className="text-emerald-300 font-bold">Voting is open{s.voting_end ? ` until ${s.voting_end}` : ""} — one vote per category.</span>
            : s.flags.nominations_open ? <span className="text-sky-300 font-bold">Nominations are open — voting starts soon.</span>
            : s.flags.results_published ? <span className="text-amber-300 font-bold">Results are in!</span>
            : <span className="text-zinc-500">Voting is closed.</span>}
        </div>
      </div>

      {result && result.winner && (
        <div className="bg-amber-400/10 border border-amber-400/30 rounded-2xl p-6 mb-8" data-testid="category-winner">
          <p className="overline mb-2">Winner</p>
          <Link to={`/${result.winner.coll}/${result.winner.slug}`} className="flex items-center gap-4">
            {result.winner.image ? <img src={fileUrl(result.winner.image)} alt="" className="w-16 h-16 rounded-xl object-cover" /> : <Trophy size={28} className="text-amber-400" />}
            <div><p className="font-display font-bold text-2xl">{result.winner.name}</p>
              {settings.public_vote_counts && <p className="text-sm text-zinc-400">{result.winner.votes} votes</p>}</div>
          </Link>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {nominees.map((n) => (
          <div key={n.id} className="bg-[#111] border border-white/10 rounded-2xl overflow-hidden" data-testid={`nominee-${n.slug}`}>
            <div className="relative aspect-video">
              {n.image ? <img src={fileUrl(n.image)} alt={n.name} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-white/5" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
              <div className="absolute top-2 right-2 flex gap-1.5">
                {n.vip && <span className="bg-amber-400 text-black text-[10px] font-extrabold px-2 py-0.5 rounded-full">VIP</span>}
                <span className="bg-sky-500/30 text-sky-200 text-[10px] font-bold px-2 py-0.5 rounded-full">Nominated</span>
              </div>
              <div className="absolute bottom-0 p-3">
                <p className="font-display font-bold text-lg leading-tight">{n.name}</p>
                <p className="text-xs text-zinc-400 flex items-center gap-1 mt-0.5">
                  {n.genre && <><Music size={10} /> {n.genre} · </>}{n.venue_type && <>{n.venue_type} · </>}
                  <MapPin size={10} /> {n.city}{n.state ? `, ${n.state}` : ""}
                </p>
              </div>
            </div>
            <div className="p-3 flex items-center justify-between gap-2">
              <div className="text-xs text-zinc-500">
                {"vote_count" in n && <span className="font-bold text-zinc-300" data-testid={`votes-${n.slug}`}>{n.vote_count} vote{n.vote_count === 1 ? "" : "s"}</span>}
              </div>
              <div className="flex gap-2 items-center">
                <ShareNominee n={n} s={s} c={c} />
                <Link to={`/${n.coll}/${n.slug}`} className="text-xs border border-white/15 px-3 py-1.5 rounded-full text-zinc-300" data-testid={`profile-${n.slug}`}>Profile</Link>
                {votingOpen && (n.my_vote ? (
                  settings.allow_vote_undo && <button onClick={() => unvote(n)} className="text-xs bg-emerald-500/20 text-emerald-300 px-3 py-1.5 rounded-full flex items-center gap-1" data-testid={`unvote-${n.slug}`}><Undo2 size={11} /> Voted — undo</button>
                ) : (
                  <button onClick={() => vote(n)} className="text-xs bg-rose-500 hover:bg-rose-600 font-bold px-3 py-1.5 rounded-full flex items-center gap-1" data-testid={`vote-${n.slug}`}><Vote size={11} /> Vote</button>
                ))}
              </div>
            </div>
          </div>
        ))}
        {nominees.length === 0 && <p className="text-zinc-500 col-span-full text-center py-14" data-testid="no-nominees">No nominees yet — be the first to nominate someone from their profile page!</p>}
      </div>
    </div>
  );
}
